import {
  Component,
  inject,
  input,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { SectionLockAlertComponent } from '@shared/dasboard-sections/components/section-lock-alert/section-lock-alert.component';

import { ProbateSections } from '@app/core/probate/models/probate-sections.models';
import { TabComponent } from '@shared/components/tabs/tab/tab.component';
import { SectionLockDirective } from '@shared/directives/section-lock.directive';
import { SectionLockedPipe } from '@shared/pipes/section-locked.pipe';
import { TabsComponent } from '@shared/components/tabs/tabs.component';
import { InputFileComponent } from '@shared/components/input-file/input-file.component';
import { CheckboxComponent } from '@shared/components/checkbox';
import { SkeletonComponent } from '@shared/components/skeleton/skeleton.component';
import { SignatureContainerComponent } from '../signature-container/signature-container.component';
import { SignatureTypedTextComponent } from '../signature-typed-text/signature-typed-text.component';

@Component({
  selector: 'app-signature-form',
  imports: [
    SectionLockAlertComponent,
    ReactiveFormsModule,
    SectionLockDirective,
    SectionLockAlertComponent,
    SectionLockedPipe,
    TabsComponent,
    SignatureTypedTextComponent,
    TabComponent,
    SignatureContainerComponent,
    InputFileComponent,
    CheckboxComponent,
    SkeletonComponent,
  ],
  templateUrl: './signature-form.component.html',
})
export class SignatureFormComponent {
  private _fb = inject(FormBuilder);
  activeTabTitle = signal<string>('');
  areSignatureTabsPristine = signal(true);
  isLoading = input<boolean>();
  formSubmit = output<FormData>();

  readonly Section = ProbateSections;

  @ViewChild(SignatureContainerComponent)
  signatureContainer?: SignatureContainerComponent;
  @ViewChild(SignatureTypedTextComponent)
  signatureTypedText?: SignatureTypedTextComponent;

  signatureForm = this._fb.group({
    signature_file: this._fb.control<File | null>(null),
    terms: this._fb.control<boolean>(false),
  });

  private _getSignatureBlobFromActiveTab() {
    const formData = new FormData();
    switch (this.activeTabTitle()) {
      case this.tabTitles[1]: {
        if (this._signaturePad) {
          if (!this.areSignatureTabsPristine()) {
            const imageBlob = this._getDrawedSignatureBlob();
            if (imageBlob) {
              this._appendSignatureFieldsToFormData(formData, imageBlob);
            }
          }
        }
        break;
      }
      case this.tabTitles[0]: {
        if (!this.areSignatureTabsPristine()) {
          const signatureBlob = this.signatureTypedText?.getImageBlob();
          if (signatureBlob) {
            this._appendSignatureFieldsToFormData(formData, signatureBlob);
          }
        }

        break;
      }
      case this.tabTitles[2]: {
        const signatureFile = this.signatureForm.get('signature_file')?.value;
        if (signatureFile instanceof File) {
          this._appendSignatureFieldsToFormData(formData, signatureFile);
        }

        break;
      }
    }
    return formData;
  }

  _getPopulatedSignatureFormData() {
    const formData = this._getSignatureBlobFromActiveTab();
    return formData;
  }

  private _appendSignatureFieldsToFormData(
    formData: FormData,
    signatureFile: Blob,
  ) {
    formData.append('signature', signatureFile);
    const termsChecked = this.signatureForm.get('terms')?.value;
    formData.append(
      'agree_to_electronic_signature',
      termsChecked ? 'true' : 'false',
    );
  }

  // Bound method for use with the click-and-navigate directive
  submitSignatureBound = () => {
    this.submitSignature();
  };

  submitSignature() {
    if (this.isSignatureFormInvalid) {
      this.signatureForm.markAllAsTouched();
      return;
    }

    const formData = this._getPopulatedSignatureFormData();
    this.formSubmit.emit(formData);
    this.signatureForm.reset();
    this.resetSignatures();
  }

  get isSignatureFormInvalid() {
    const termsChecked = this.signatureForm.get('terms')?.value;
    const hasSignature = this._hasValidSignature();

    return this.signatureForm.invalid || !termsChecked || !hasSignature;
  }

  private _hasValidSignature(): boolean {
    // Check if there's a valid signature based on the active tab
    switch (this.activeTabTitle()) {
      case this.tabTitles[0]: // Typed text
        return !!this.signatureTypedText?.getImageBlob();
      case this.tabTitles[1]: // Draw signature
        return !!this._getDrawedSignatureBlob();
      case this.tabTitles[2]: // Upload signature
        return !!this.signatureForm.get('signature_file')?.value;
      default:
        return false;
    }
  }

  private get _signaturePad() {
    return this.signatureContainer?.signaturePad;
  }
  tabChangeValue(tab: TabComponent) {
    this.activeTabTitle.set(tab.title()!);
    this.resetSignatures();
    this.areSignatureTabsPristine.set(true);
  }

  resetSignatures() {
    this.signatureForm.get('signature_file')?.setValue(null);
    this.signatureTypedText?.resetAll();
    this.signatureContainer?.clearSignature();
    setTimeout(() => {
      this.signatureContainer?.resizeCanvas();
    }, 0);
  }
  private _getDrawedSignatureBlob() {
    return this.signatureContainer?.getImageBlob();
  }

  readonly acceptedDocumentTypes = {
    doc: true,
    docx: true,
    jpg: true,
    jpeg: true,
    png: true,
    pdf: true,
    tiff: true,
  };

  readonly acceptedSignatureTypes = {
    jpg: true,
    jpeg: true,
    png: true,
  };

  readonly tabTitles = [
    'Used Typed Text',
    'Draw my Signature',
    'Upload my Signature',
  ];
}
